import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import SportChips from "../../components/SportChips";
import toast from "react-hot-toast";
import { useParams, useNavigate } from "react-router-dom";
import {
  getUserById,
  updateUser,
  adminResetUserPassword,
  assignRole,
  removeRole,
  getUserPoints,
  adjustUserPoints,
  getUserSessions,
  getCoachTeams,
  uploadProfileImage,
  getUserStatusHistory,
  deleteUser,
  getUserAdminImpact,
  setUserEmailVerified,
  resendUserVerification,
  type User,
  type AdminImpact,
  type PointsHistoryResponse,
  type SessionsHistoryResponse,
  type CoachTeamsResponse,
  type StatusHistoryEntry,
} from "../../services/users";
import { getClubById } from "../../services/clubs";
import { assignUserToTeam, getClubTeams, updateTeam, getTeamById, type Team } from "../../services/teams";
import api, { changeUserStatus } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import { AdminCard, SectionCard, StatCard, StatGrid } from "../../components/admin/AdminCard";
import { formatDate } from "../../lib/dateFormat";
import LoginAsButton from "../../components/LoginAsButton";
import Coach360 from "../../components/club/Coach360";
import UserEmailsPanel from "../../components/admin/UserEmailsPanel";
// Inline SVG icons to avoid lucide-react dependency
const Icons = {
  ArrowLeft: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  ),
  User: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  Shield: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  Activity: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  Star: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
    </svg>
  ),
  Users: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  Edit: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  Save: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
    </svg>
  ),
  X: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  Plus: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  ),
  Trash2: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  ),
  Calendar: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  MapPin: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  Phone: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  ),
  Mail: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  Clock: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  AlertTriangle: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ),
  CheckCircle: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  XCircle: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  Camera: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  Upload: ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
  ),
};

// Alias for UserIcon
const UserIcon = Icons.User;

type TabType = "overview" | "general" | "roles" | "points" | "sessions" | "profile" | "teams" | "emails";

export default function UserDetails() {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  // Club admins reach this page from /dashboard/admin-club/coaches and need
  // the club-admin sidebar. Platform admins get the global sidebar from
  // App.tsx, so render bare for them.
  //
  // IMPORTANT: do NOT define a wrapper *component* (e.g. `PageWrapper`)
  // inside the function body. Each render would produce a new component
  // identity, React would remount the inner ClubAdminLayout, and that
  // layout's mount-time effects (e.g. close-menu-on-pathname-change) would
  // immediately reset the mobile sidebar state — the hamburger would
  // appear non-functional. Use a function that returns JSX with the
  // imported ClubAdminLayout type instead.
  const { activeRole, activeClubId, user: currentUser } = useAuth();
  const wrapClubAdminLayout = activeRole === "club_admin";
  const layoutShell = (children: React.ReactNode) =>
    wrapClubAdminLayout ? <ClubAdminLayout>{children}</ClubAdminLayout> : <>{children}</>;

  const [activeTab, setActiveTab] = useState<TabType>("general");
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    email: "",
    full_name: "",
    phone: "",
    gender: "",
    date_of_birth: "",
  });
  // Password reset state (admin can set a new password for any user from the
  // General edit panel). Kept separate from editForm so an empty value means
  // "don't touch the password" — only PATCHed when the field has content.
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);
  const [showAddRole, setShowAddRole] = useState(false);
  const [newRole, setNewRole] = useState({
    code: "athlete",
    scope_type: "club" as "platform" | "club" | "team",
    scope_id: "",
  });
  const [pointsData, setPointsData] = useState<PointsHistoryResponse | null>(null);
  const [sessionsData, setSessionsData] = useState<SessionsHistoryResponse | null>(null);
  const [coachTeamsData, setCoachTeamsData] = useState<CoachTeamsResponse | null>(null);
  const [approvingJoinId, setApprovingJoinId] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState("");

  /** Accept a child who is waiting, without leaving the coach's roster. */
  const approveJoin = async (membershipId: string) => {
    setApprovingJoinId(membershipId);
    try {
      await api.post(`/join-links/requests/${membershipId}/approve`);
      toast.success(t("userDetails.approved", "Sportiv acceptat."));
      setCoachTeamsData(null); // forces the roster to reload with the new state
    } catch (e: any) {
      toast.error(e?.response?.data?.error || t("userDetails.approveFailed", "Nu am putut accepta cererea."));
    }
    setApprovingJoinId(null);
  };
  const [loadingPoints, setLoadingPoints] = useState(false);
  // Manual points adjustment (Puncte tab)
  const [showAdjust, setShowAdjust] = useState(false);
  const [adjustSign, setAdjustSign] = useState<"add" | "subtract">("add");
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustReasonPreset, setAdjustReasonPreset] = useState("");
  const [adjustCustomReason, setAdjustCustomReason] = useState("");
  const [adjusting, setAdjusting] = useState(false);
  const [adjustError, setAdjustError] = useState<string | null>(null);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingCoachTeams, setLoadingCoachTeams] = useState(false);
  const [clubDetails, setClubDetails] = useState<Record<string, any>>({});
  // Team scope lookup: roles can be scoped to a team id; we resolve each id
  // into the full Team (name, coach) so the Roles tab renders friendly names
  // with clickable links instead of "team (uuid…)".
  const [teamDetails, setTeamDetails] = useState<Record<string, Team>>({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Inline confirmation for status-change & account-delete actions.
  // We render the confirm panel where the action button used to be, so the
  // workflow stays in the page even when browser confirm dialogs are blocked
  // by ad-blockers/extensions.
  const [pendingStatusAction, setPendingStatusAction] = useState<"active" | "demo" | "inactive" | null>(null);
  const [pendingDelete, setPendingDelete] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  // "How many teams / sessions does this user touch?" — populated on the
  // first confirm-chip open so the admin sees the blast radius.
  const [impact, setImpact] = useState<AdminImpact | null>(null);
  const [impactLoading, setImpactLoading] = useState(false);

  const ensureImpactLoaded = async () => {
    if (!user || impact !== null) return;
    setImpactLoading(true);
    try {
      const data = await getUserAdminImpact(user.id);
      setImpact(data);
    } catch (err) {
      console.error("Failed to load admin impact:", err);
    } finally {
      setImpactLoading(false);
    }
  };

  // Compose the post-delete destination. A club_admin landed here from
  // their own coaches/athletes lists, so we push them back there; a
  // platform_admin returns to the global users list.
  const postDeleteDestination = (): string => {
    if (activeRole === "club_admin" && user) {
      const isCoach = user.roles?.some((r) => r.code === "coach");
      const isAthlete = user.roles?.some((r) => r.code === "athlete");
      if (isCoach) return "/dashboard/admin-club/coaches";
      if (isAthlete) return "/dashboard/admin-club/athletes";
      return "/dashboard/admin-club";
    }
    return "/admin/users";
  };

  const performStatusChange = async (status: "active" | "demo" | "inactive") => {
    if (!user) return;
    setStatusBusy(true);
    try {
      await changeUserStatus(user.id, status);
      window.location.reload();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Eroare");
      setStatusBusy(false);
      setPendingStatusAction(null);
    }
  };

  const performDelete = async () => {
    if (!user) return;
    setDeleteBusy(true);
    try {
      await deleteUser(user.id);
      navigate(postDeleteDestination(), { replace: true });
    } catch (err: any) {
      alert(err?.response?.data?.error || "Eroare");
      setDeleteBusy(false);
      setPendingDelete(false);
    }
  };

  // Team/Club assignment state
  const [showAddToTeam, setShowAddToTeam] = useState(false);
  const [showAddToClub, setShowAddToClub] = useState(false);
  const [availableTeams, setAvailableTeams] = useState<Team[]>([]);
  const [availableClubs, setAvailableClubs] = useState<any[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [selectedClubId, setSelectedClubId] = useState("");
  const [assignmentRole, setAssignmentRole] = useState<"athlete" | "coach">("athlete");
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [loadingClubs, setLoadingClubs] = useState(false);

  // Coach team allocation state
  const [showAllocateTeams, setShowAllocateTeams] = useState(false);
  const [clubTeamsForAllocation, setClubTeamsForAllocation] = useState<Team[]>([]);
  const [allocatingTeam, setAllocatingTeam] = useState<string | null>(null);

  // Status history state
  const [statusHistory, setStatusHistory] = useState<StatusHistoryEntry[]>([]);
  const [loadingStatusHistory, setLoadingStatusHistory] = useState(false);

  // Reset active tab when navigating to a different user profile
  useEffect(() => {
    setActiveTab("general");
  }, [userId]);

  /**
   * Which tab the page opens on, decided once we know who the subject is.
   *
   * A coach opens on their 360 view: "what does this person do here" is the
   * question somebody has when they click a coach's name, and the account
   * fields are the answer to a different one. Everyone else keeps the details
   * tab they had. The ref makes this a once-per-subject default rather than a
   * rule that would snap the tab back every time the user object is refetched.
   */
  const defaultedTabFor = React.useRef<string | null>(null);
  useEffect(() => {
    if (!user || defaultedTabFor.current === user.id) return;
    defaultedTabFor.current = user.id;
    if (user.coach_profiles) setActiveTab("overview");
  }, [user]);

  useEffect(() => {
    if (userId) fetchUserData();
  }, [userId]);

  useEffect(() => {
    if (user && activeTab === "points" && !pointsData) fetchPointsData();
  }, [activeTab, user]);

  useEffect(() => {
    if (user && activeTab === "sessions" && !sessionsData) fetchSessionsData();
  }, [activeTab, user]);

  useEffect(() => {
    if (user && activeTab === "profile" && user.athlete_profiles) fetchClubDetails();
  }, [activeTab, user]);

  // Also fetch club details up-front so the Roles tab and the coach
  // General-tab summary can show real club names instead of raw ids.
  useEffect(() => {
    if (user) fetchClubDetails();
  }, [user]);

  // Team-scoped roles need a team name + assigned coach for the Roles tab.
  useEffect(() => {
    if (!user?.roles) return;
    const ids = user.roles
      .filter((r) => r.scope_type === "team" && r.scope_id && !teamDetails[r.scope_id])
      .map((r) => r.scope_id as string);
    if (ids.length === 0) return;
    (async () => {
      const results = await Promise.allSettled(ids.map((id) => getTeamById(id)));
      const next: Record<string, Team> = { ...teamDetails };
      results.forEach((r, idx) => {
        if (r.status === "fulfilled") next[ids[idx]] = r.value;
      });
      setTeamDetails(next);
    })();
  }, [user]);

  useEffect(() => {
    if (user && activeTab === "teams" && user.coach_profiles && !coachTeamsData) fetchCoachTeamsData();
  }, [activeTab, user]);

  // Coach summary (club, sports, totals) is shown in the General tab too,
  // so pre-fetch coach teams data as soon as we know the user is a coach.
  useEffect(() => {
    if (user && user.coach_profiles && !coachTeamsData) fetchCoachTeamsData();
  }, [user]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUserById(userId!);
      setUser(data);
      setEditForm({
        email: data.email || "",
        full_name: data.full_name || "",
        phone: data.phone || "",
        gender: data.gender || "",
        date_of_birth: data.date_of_birth || "",
      });
      // Also fetch status history
      fetchStatusHistory();
    } catch (err) {
      setError(t("userDetails.failedToLoad", "Nu s-au putut încărca detaliile utilizatorului"));
    } finally {
      setLoading(false);
    }
  };

  const fetchPointsData = async () => {
    try {
      setLoadingPoints(true);
      const data = await getUserPoints(userId!, { limit: 50 });
      setPointsData(data);
    } finally {
      setLoadingPoints(false);
    }
  };

  // Predefined reasons for a manual adjustment; "custom" reveals a free-text field.
  const POINTS_REASON_PRESETS: { value: string; label: string }[] = [
    { value: "Comportament exemplar", label: "Comportament exemplar" },
    { value: "Efort deosebit", label: "Efort deosebit" },
    { value: "Progres remarcabil", label: "Progres remarcabil" },
    { value: "Voluntariat / ajutor", label: "Voluntariat / ajutor" },
    { value: "Corecție puncte", label: "Corecție puncte" },
    { value: "Abatere disciplinară", label: "Abatere disciplinară" },
    { value: "Absență nemotivată", label: "Absență nemotivată" },
    { value: "custom", label: "Alt motiv (scrie)…" },
  ];

  const resetAdjustForm = () => {
    setAdjustSign("add");
    setAdjustAmount("");
    setAdjustReasonPreset("");
    setAdjustCustomReason("");
    setAdjustError(null);
  };

  const handleAdjustPoints = async (e: React.FormEvent) => {
    e.preventDefault();
    setAdjustError(null);
    const amount = parseInt(adjustAmount, 10);
    if (!Number.isFinite(amount) || amount <= 0) {
      setAdjustError(t("userDetails.adjustAmountInvalid", "Introdu un număr de puncte mai mare ca 0."));
      return;
    }
    const reason = (adjustReasonPreset === "custom" ? adjustCustomReason : adjustReasonPreset).trim();
    if (!reason) {
      setAdjustError(t("userDetails.adjustReasonRequired", "Alege sau scrie un motiv."));
      return;
    }
    const delta = adjustSign === "add" ? amount : -amount;
    try {
      setAdjusting(true);
      await adjustUserPoints(userId!, { delta, reason });
      await fetchPointsData();
      setShowAdjust(false);
      resetAdjustForm();
    } catch (err: any) {
      setAdjustError(err?.response?.data?.error || t("userDetails.adjustFailed", "Nu s-a putut ajusta punctajul."));
    } finally {
      setAdjusting(false);
    }
  };

  const fetchSessionsData = async () => {
    try {
      setLoadingSessions(true);
      const data = await getUserSessions(userId!, { limit: 50 });
      setSessionsData(data);
    } finally {
      setLoadingSessions(false);
    }
  };

  const fetchClubDetails = async () => {
    if (!user) return;
    try {
      // Collect every club id referenced by the user: athlete profile,
      // coach profile, and any club-scoped role. Roles tab + General tab
      // both need names instead of raw ids.
      const ids = new Set<string>();
      if (user.athlete_profiles?.club_id) ids.add(user.athlete_profiles.club_id);
      if ((user.coach_profiles as any)?.club_id) ids.add((user.coach_profiles as any).club_id);
      user.roles?.forEach((r) => {
        if (r.scope_type === "club" && r.scope_id) ids.add(r.scope_id);
      });

      const missing = Array.from(ids).filter((id) => !clubDetails[id]);
      if (missing.length === 0) return;

      const results = await Promise.allSettled(missing.map((id) => getClubById(id)));
      const next: Record<string, any> = { ...clubDetails };
      results.forEach((r, idx) => {
        if (r.status === "fulfilled") next[missing[idx]] = r.value;
      });
      setClubDetails(next);
    } catch (err) {
      console.error("Failed to fetch club details:", err);
    }
  };

  const fetchCoachTeamsData = async () => {
    try {
      setLoadingCoachTeams(true);
      const data = await getCoachTeams(userId!);
      setCoachTeamsData(data);
    } catch (err) {
      console.error("Failed to fetch coach teams:", err);
    } finally {
      setLoadingCoachTeams(false);
    }
  };

  const fetchStatusHistory = async () => {
    try {
      setLoadingStatusHistory(true);
      const data = await getUserStatusHistory(userId!);
      setStatusHistory(data);
    } catch (err: any) {
      // Status history is platform-admin-only by design (it's a global audit
      // log). Club admins land here when they open a coach's profile, hit
      // 403, and would otherwise see a scary red console error. Suppress that
      // log for the expected 403 case while keeping real errors visible.
      const status = err?.response?.status;
      if (status !== 403) console.error("Failed to fetch status history:", err);
    } finally {
      setLoadingStatusHistory(false);
    }
  };

  /**
   * Confirm the address by hand, or send a new link.
   *
   * An expired verification link leaves an account that nobody can sign into
   * and nobody can fix from the UI — the password works, the login refuses.
   * Both ways out live here.
   */
  const [verifyBusy, setVerifyBusy] = useState<"confirm" | "resend" | null>(null);

  const handleConfirmEmail = async () => {
    setVerifyBusy("confirm");
    try {
      await setUserEmailVerified(userId!, true);
      setUser((u) => (u ? { ...u, email_verified: true } : u));
      toast.success(t("userDetails.emailConfirmed", "Adresa de email a fost confirmată. Utilizatorul se poate autentifica."));
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t("userDetails.emailConfirmFailed", "Nu am putut confirma adresa."));
    }
    setVerifyBusy(null);
  };

  const handleResendVerification = async () => {
    setVerifyBusy("resend");
    try {
      await resendUserVerification(userId!);
      toast.success(t("userDetails.verificationResent", "Am trimis un link nou de confirmare."));
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t("userDetails.verificationResendFailed", "Nu am putut trimite emailul."));
    }
    setVerifyBusy(null);
  };

  /** The password on its own, with its own button and its own errors. */
  const handleSavePassword = async () => {
    const pw = newPassword.trim();
    const pw2 = confirmPassword.trim();
    setPasswordError("");

    if (pw.length < 8) {
      setPasswordError(t("userDetails.passwordTooShort", "Parola trebuie să aibă cel puțin 8 caractere."));
      return;
    }
    if (pw !== pw2) {
      setPasswordError(t("userDetails.passwordsMismatch", "Cele două parole nu sunt identice."));
      return;
    }

    setResettingPassword(true);
    try {
      await adminResetUserPassword(userId!, pw);
      setNewPassword("");
      setConfirmPassword("");
      toast.success(t("userDetails.passwordSaved", "Parola a fost schimbată."));
    } catch (err: any) {
      setPasswordError(
        err?.response?.data?.error || t("userDetails.passwordFailed", "Nu am putut schimba parola.")
      );
    }
    setResettingPassword(false);
  };

  const handleSaveEdit = async () => {
    try {
      // Only send email if it actually changed — backend has a unique
      // constraint on users.email and PATCHing the same value would 409
      // when prisma upserts hit the unique index in some cases.
      const payload: Record<string, string> = {};
      if (editForm.email && editForm.email !== user?.email) payload.email = editForm.email.trim();
      if (editForm.full_name !== undefined) payload.full_name = editForm.full_name;
      if (editForm.phone !== undefined) payload.phone = editForm.phone;
      if (editForm.gender !== undefined) payload.gender = editForm.gender;
      if (editForm.date_of_birth !== undefined) payload.date_of_birth = editForm.date_of_birth;
      await updateUser(userId!, payload);
      await fetchUserData();
      setIsEditing(false);
    } catch (err: any) {
      alert(err.response?.data?.error || "Nu s-a putut actualiza utilizatorul");
    }
  };

  const handleAddRole = async () => {
    try {
      await assignRole(userId!, newRole);
      await fetchUserData();
      setShowAddRole(false);
      setNewRole({ code: "athlete", scope_type: "club", scope_id: "" });
    } catch (err: any) {
      alert(err.response?.data?.error || "Nu s-a putut atribui rolul");
    }
  };

  const handleRemoveRole = async (roleId: string) => {
    if (!window.confirm(t("userDetails.confirmRemoveRole", "Sigur doriți să eliminați acest rol?"))) return;
    try {
      await removeRole(userId!, roleId);
      await fetchUserData();
    } catch (err: any) {
      alert(err.response?.data?.error || "Nu s-a putut elimina rolul");
    }
  };

  // Load teams the subject user could be assigned to. Earlier this used
  // `searchTeams()` which hits /search/teams, but that route is commented
  // out in server.ts — silently 404'd and the dropdown was always empty.
  // Scope the call to the subject's known club via getClubTeams (which is
  // wired up and serves both club_admins and platform_admins).
  const loadTeamsForAssignment = async () => {
    try {
      setLoadingTeams(true);
      // Prefer the coach's club, then the athlete's club, then the first
      // club-scoped role's scope_id. Bail out only if we genuinely don't
      // know which club this user belongs to.
      const clubId =
        (user?.coach_profiles as any)?.club_id ||
        (user?.athlete_profiles as any)?.club_id ||
        user?.roles?.find(r => r.scope_type === "club" && r.scope_id)?.scope_id ||
        null;
      if (!clubId) {
        setAvailableTeams([]);
        return;
      }
      const teams = await getClubTeams(clubId);
      setAvailableTeams(teams);
    } catch (err) {
      console.error("Failed to load teams:", err);
      setAvailableTeams([]);
    } finally {
      setLoadingTeams(false);
    }
  };

  // Load clubs for assignment modal
  const loadClubsForAssignment = async () => {
    try {
      setLoadingClubs(true);
      const { data } = await api.get("/clubs");
      // /clubs returns [{ club: {...}, role: "..." }] for non-platform-admins
      // — unwrap to a flat list of club objects so the dropdown renders names.
      const flat = Array.isArray(data)
        ? data.map((row: any) => row?.club ?? row).filter(Boolean)
        : [];
      setAvailableClubs(flat);
    } catch (err) {
      console.error("Failed to load clubs:", err);
      setAvailableClubs([]);
    } finally {
      setLoadingClubs(false);
    }
  };

  // Load all club teams for coach allocation
  const loadClubTeamsForAllocation = async () => {
    if (!user?.coach_profiles?.club_id) return;
    try {
      setLoadingTeams(true);
      const teams = await getClubTeams(user.coach_profiles.club_id);
      setClubTeamsForAllocation(teams);
    } catch (err) {
      console.error("Failed to load club teams:", err);
    } finally {
      setLoadingTeams(false);
    }
  };

  // Toggle team allocation for coach
  const toggleTeamAllocation = async (teamId: string, currentlyAssigned: boolean) => {
    if (!userId) return;
    setAllocatingTeam(teamId);
    try {
      // If currently assigned to this coach, remove the assignment (set coach_id to null)
      // If not assigned, assign this coach to the team
      await updateTeam(teamId, { coach_id: currentlyAssigned ? "" : userId });
      // Refresh data
      await loadClubTeamsForAllocation();
      if (coachTeamsData) {
        await fetchCoachTeamsData();
      }
    } catch (err: any) {
      alert(err.response?.data?.error || "Nu s-a putut actualiza alocarea echipei");
    } finally {
      setAllocatingTeam(null);
    }
  };

  // Open coach team allocation modal
  const openAllocateTeamsModal = () => {
    setShowAllocateTeams(true);
    loadClubTeamsForAllocation();
  };

  // Handle adding user to team
  // Re-fetch a user's team-scope details (coaches, sport, age_group) when
  // their role list changes. The Roles tab effect only fires on
  // user changes, so without this the row stays stale after assign/remove.
  const refreshTeamScopeDetails = async (rolesForUser: User["roles"] | undefined) => {
    if (!rolesForUser) return;
    const ids = rolesForUser
      .filter((r) => r.scope_type === "team" && r.scope_id)
      .map((r) => r.scope_id as string);
    if (ids.length === 0) return;
    const results = await Promise.allSettled(ids.map((id) => getTeamById(id)));
    const next: Record<string, Team> = { ...teamDetails };
    results.forEach((r, idx) => {
      if (r.status === "fulfilled") next[ids[idx]] = r.value;
    });
    setTeamDetails(next);
  };

  const handleAddToTeam = async () => {
    if (!selectedTeamId) return;
    try {
      await assignUserToTeam(selectedTeamId, userId!, assignmentRole);
      const fresh = await getUserById(userId!);
      setUser(fresh);
      await refreshTeamScopeDetails(fresh.roles);
      setShowAddToTeam(false);
      setSelectedTeamId("");
    } catch (err: any) {
      alert(err.response?.data?.error || "Nu s-a putut adăuga utilizatorul în echipă");
    }
  };

  // Handle adding user to club
  const handleAddToClub = async () => {
    if (!selectedClubId) return;
    try {
      await assignRole(userId!, {
        code: assignmentRole,
        scope_type: "club",
        scope_id: selectedClubId,
      });
      await fetchUserData();
      setShowAddToClub(false);
      setSelectedClubId("");
    } catch (err: any) {
      alert(err.response?.data?.error || "Nu s-a putut adăuga utilizatorul în club");
    }
  };

  // Open Add to Team modal
  const openAddToTeamModal = () => {
    // Pre-select the role matching the user's profile type so a coach
    // profile defaults to "coach" instead of forcing the admin to flip
    // the dropdown every time.
    if (user?.coach_profiles) {
      setAssignmentRole("coach");
    } else if (user?.athlete_profiles) {
      setAssignmentRole("athlete");
    }
    setShowAddToTeam(true);
    loadTeamsForAssignment();
  };

  // Open Add to Club modal
  const openAddToClubModal = () => {
    if (user?.coach_profiles) {
      setAssignmentRole("coach");
    } else if (user?.athlete_profiles) {
      setAssignmentRole("athlete");
    }
    setShowAddToClub(true);
    loadClubsForAssignment();
  };

  // Toggle the Advanced role assignment block. When opening, pre-seed the
  // role code from the subject's profile and load BOTH the clubs and the
  // teams list so the scope_id picker has options regardless of which scope
  // the admin selects next.
  const toggleAdvancedRole = () => {
    const opening = !showAddRole;
    setShowAddRole(opening);
    if (opening) {
      const defaultCode = user?.coach_profiles
        ? "coach"
        : user?.athlete_profiles
          ? "athlete"
          : "club_admin";
      setNewRole({ code: defaultCode, scope_type: "club", scope_id: "" });
      loadTeamsForAssignment();
      loadClubsForAssignment();
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      alert("Vă rugăm să selectați un fișier imagine");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Imaginea trebuie să fie mai mică de 5MB");
      return;
    }

    try {
      setUploadingImage(true);
      const { url } = await uploadProfileImage(file);
      await updateUser(userId!, { profile_url: url });
      await fetchUserData();
    } catch (err: any) {
      alert(err.response?.data?.error || "Nu s-a putut încărca imaginea");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const formatActionType = (actionType: string | null | undefined) => {
    if (!actionType) return "—";
    return actionType.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      present: "bg-emerald-100 text-emerald-700 border border-emerald-200",
      late: "bg-amber-100 text-amber-700 border border-amber-200",
      absent: "bg-rose-100 text-rose-700 border border-rose-200",
      excused: "bg-indigo-100 text-indigo-700 border border-indigo-200",
    };
    return styles[status as keyof typeof styles] || "bg-slate-100 text-slate-600 border border-slate-200";
  };

  const calculateAge = (dateOfBirth: string | null): number | null => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  if (loading) {
    return layoutShell(
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
          <p className="text-slate-500">{t("userDetails.loading", "Se încarcă detaliile utilizatorului...")}</p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return layoutShell(
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-6">
        <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center">
          <Icons.XCircle className="w-8 h-8 text-rose-500" />
        </div>
        <p className="text-lg text-slate-700">{error || t("userDetails.notFound", "Utilizatorul nu a fost găsit")}</p>
        <AdminButton onClick={() => navigate(-1)} icon={<Icons.ArrowLeft className="w-4 h-4" />}>
          {t("common.back", "Înapoi")}
        </AdminButton>
      </div>
    );
  }

  const isAthlete = !!user.athlete_profiles;
  const isCoach = !!user.coach_profiles;
  // Demo / inactive controls belong to subscription-level accounts
  // (club_admin / platform_admin). Athletes and coaches inherit access from
  // their club, so the per-user demo toggle is meaningless for them.
  const isSubscriptionAccount = !!user.roles?.some(r => r.code === "club_admin" || r.code === "platform_admin");
  // Mirrors the server rule in PATCH /users/:id/status.
  const viewingSelf = currentUser?.id === user.id;
  const targetIsClubAdmin = !!user.roles?.some((r) => r.code === "club_admin");
  const canChangeThisStatus =
    activeRole === "platform_admin" || (!viewingSelf && !targetIsClubAdmin);

  const tabs = [
    // The coach overview leads, because it is what the page is usually opened
    // for. It only exists for coaches; for everyone else nothing moves.
    ...(isCoach ? [
      { key: "overview", label: t("userDetails.coachOverview", "Prezentare"), icon: Icons.Activity },
    ] : []),
    { key: "general", label: t("userDetails.generalInfo", "Informații generale"), icon: UserIcon },
    { key: "roles", label: t("userDetails.roles", "Roluri"), icon: Icons.Shield },
    ...(isCoach ? [
      { key: "teams", label: t("userDetails.teamsAndAthletes", "Echipe și sportivi"), icon: Icons.Users },
    ] : []),
    ...(isAthlete ? [
      { key: "profile", label: t("userDetails.profile", "Profil"), icon: Icons.Users },
      { key: "sessions", label: t("userDetails.sessions", "Sesiuni"), icon: Icons.Activity },
      { key: "points", label: t("userDetails.points", "Puncte"), icon: Icons.Star },
    ] : []),
    // Platform admins only: the endpoint behind it reads every message sent to
    // an address, which is more than a club admin should see of their staff.
    ...(activeRole === "platform_admin" ? [
      { key: "emails", label: t("userDetails.emails", "Emailuri"), icon: Icons.Mail },
    ] : []),
  ];

  // Status pill config, shared by the header badge and the account-status
  // section so both read the same emerald/amber/rose/indigo semantics.
  // emerald = active, indigo = demo/trial, rose = inactive.
  const STATUS_CFG: Record<string, { dot: string; chip: string; label: string }> = {
    active: { dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", label: t("userDetails.statusActive", "Activ") },
    demo: { dot: "bg-indigo-500", chip: "bg-indigo-50 text-indigo-700 border-indigo-200", label: t("userDetails.statusDemo", "Demo") },
    inactive: { dot: "bg-rose-500", chip: "bg-rose-50 text-rose-700 border-rose-200", label: t("userDetails.statusInactive", "Inactiv") },
  };
  const statusCfg = STATUS_CFG[user.status || "active"] || STATUS_CFG.active;

  // Distinct role codes → labelled pills for the profile header.
  const headerRoleCodes = Array.from(new Set((user.roles || []).map((r) => r.code)));
  // The club this subject belongs to (coach club, athlete club, or first
  // club-scoped role) — surfaced as a header badge for quick context.
  const headerClubId =
    (user.coach_profiles as any)?.club_id ||
    (user.athlete_profiles as any)?.club_id ||
    user.roles?.find((r) => r.scope_type === "club" && r.scope_id)?.scope_id ||
    null;
  const headerClubName =
    coachTeamsData?.coach.club?.name || (headerClubId ? clubDetails[headerClubId]?.name : null) || null;

  return layoutShell(
    <div className={`${wrapClubAdminLayout ? "" : "min-h-screen bg-slate-50 p-3 sm:p-6"} overflow-x-hidden`}>
      <div className="mx-auto max-w-[1500px]">
        {/* Header */}
        <AdminPageHeader
          bareIcon
          title={user.full_name || user.email}
          subtitle={
            <div className="space-y-2">
              <span className="flex items-center gap-2 break-all">
                <Icons.Mail className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{user.email}</span>
              </span>
              {/* Badge row: roles, current status, and the owning club */}
              <div className="flex flex-wrap items-center gap-1.5">
                {headerRoleCodes.map((code) => (
                  <span
                    key={code}
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200"
                  >
                    <Icons.Shield className="w-3 h-3" />
                    {t(`roles.${code}`, code)}
                  </span>
                ))}
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium border rounded-full ${statusCfg.chip}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot}`} />
                  {statusCfg.label}
                </span>
                {headerClubName && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                    <Icons.MapPin className="w-3 h-3" />
                    {headerClubName}
                  </span>
                )}
              </div>
              <span className="hidden sm:block text-xs text-slate-400 truncate">ID: {user.id}</span>
            </div>
          }
          icon={
            <div className="relative group flex-shrink-0">
              {user.profile_url ? (
                <img
                  src={user.profile_url}
                  alt={user.full_name || user.email}
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover shadow-lg"
                />
              ) : (
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-2xl flex items-center justify-center text-white text-xl sm:text-2xl font-bold shadow-lg">
                  {(user.full_name || user.email).charAt(0).toUpperCase()}
                </div>
              )}
              {/* Upload Overlay */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage}
                className="absolute inset-0 bg-black/50 rounded-2xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                {uploadingImage ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Icons.Camera className="w-6 h-6 text-white" />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>
          }
          actions={
            <div className="flex items-center gap-2">
              {/* Hides itself for anyone but a platform admin, on your own
                  profile, and while already borrowing somebody's session. */}
              <LoginAsButton userId={user.id} />
              <AdminButton
                variant="secondary"
                onClick={() => navigate(-1)}
                icon={<Icons.ArrowLeft className="w-4 h-4" />}
              >
                {t("common.back", "Înapoi")}
              </AdminButton>
            </div>
          }
        />

        {/* Tabs */}
        <div className="mb-4 sm:mb-6 flex gap-2 overflow-x-auto pb-2 -mx-3 sm:mx-0 px-3 sm:px-0">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as TabType)}
                className={`inline-flex items-center gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.key
                    ? "bg-indigo-600 text-white shadow-sm shadow-indigo-500/20"
                    : "bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-800 border border-slate-200"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="space-y-4 sm:space-y-6">
          {/* Every email we sent this person, with what Brevo reported back —
              and whether their invitation was ever used. The answer to "the
              coach says he never got the activation email". */}
          {activeTab === "emails" && activeRole === "platform_admin" && (
            <UserEmailsPanel userId={user.id} />
          )}

          {/* Coach overview — the 360 view, scoped to the club this page was
              opened from (or the coach's own, when it was opened directly). */}
          {activeTab === "overview" && isCoach && (
            <Coach360
              coachId={user.id}
              // A club admin always means their own club, even when the coach's
              // primary club is a different one. A platform admin has no club
              // of their own, so for them it is the coach's.
              clubId={activeRole === "platform_admin" ? headerClubId : activeClubId || headerClubId}
            />
          )}

          {/* General Tab */}
          {activeTab === "general" && (
            <div className="space-y-4 sm:space-y-6">
              <SectionCard
                title={t("userDetails.generalInformation", "Informații generale")}
                subtitle={t("userDetails.generalInfoSubtitle", "Date de contact și de identificare")}
                bodyPadded
                actions={
                  !isEditing ? (
                    <AdminButton onClick={() => setIsEditing(true)} icon={<Icons.Edit className="w-4 h-4" />}>
                      {t("common.edit", "Editează")}
                    </AdminButton>
                  ) : (
                    <>
                      <AdminButton onClick={handleSaveEdit} icon={<Icons.Save className="w-4 h-4" />}>
                        {t("common.save", "Salvează")}
                      </AdminButton>
                      <AdminButton variant="secondary" onClick={() => setIsEditing(false)} icon={<Icons.X className="w-4 h-4" />}>
                        {t("common.cancel", "Anulează")}
                      </AdminButton>
                    </>
                  )
                }
              >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { label: t("userDetails.fullName", "Nume complet"), key: "full_name", value: user.full_name, type: "text", icon: UserIcon },
                  { label: t("common.email", "Email"), key: "email", value: user.email, type: "email", icon: Icons.Mail },
                  { label: t("userDetails.phone", "Telefon"), key: "phone", value: user.phone, type: "tel", icon: Icons.Phone },
                  { label: t("userDetails.gender", "Gen"), key: "gender", value: user.gender, type: "select", options: ["", "male", "female", "other"], icon: Icons.Users },
                  { label: t("userDetails.dateOfBirth", "Data nașterii"), key: "date_of_birth", value: user.date_of_birth, type: "date", icon: Icons.Calendar },
                  { label: t("userDetails.locale", "Limbă"), key: "locale", value: user.locale, type: "text", readonly: true, icon: Icons.MapPin },
                ].map(field => {
                  const Icon = field.icon;
                  return (
                    <div key={field.key} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Icon className="w-4 h-4 text-indigo-500" />
                        <p className="text-sm font-medium text-slate-500">{field.label}</p>
                      </div>
                      {isEditing && !field.readonly ? (
                        field.type === "select" ? (
                          <select
                            value={editForm[field.key as keyof typeof editForm] || ""}
                            onChange={(e) => setEditForm({...editForm, [field.key]: e.target.value})}
                            className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          >
                            {field.options?.map(opt => <option key={opt} value={opt}>{opt || t("common.select", "Selectează...")}</option>)}
                          </select>
                        ) : (
                          <input
                            type={field.type}
                            value={editForm[field.key as keyof typeof editForm] || ""}
                            onChange={(e) => setEditForm({...editForm, [field.key]: e.target.value})}
                            className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        )
                      ) : (
                        <p className="text-base font-medium text-slate-800">
                          {field.value && field.type === "date" ? (
                            <>
                              {formatDate(new Date(field.value))}
                              {calculateAge(field.value) !== null && (
                                <span className="ml-2 text-sm text-slate-400">({calculateAge(field.value)} {t("userDetails.yearsOld", "ani")})</span>
                              )}
                            </>
                          ) : field.value || <span className="text-slate-400">—</span>}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              </SectionCard>

              {/* Setting a password is its own job, so it is its own card.
                  It used to be hidden inside "edit the profile", which meant
                  the only way to discover that you COULD change your password
                  was to go looking for it under a button about something else. */}
              <SectionCard title={t("userDetails.passwordCard", "Parolă")} bodyPadded>
                <p className="mb-3 text-xs text-slate-500">
                  {t(
                    "userDetails.passwordCardHint",
                    "Minimum 8 caractere. Noua parolă se folosește de la următoarea autentificare."
                  )}
                </p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t("userDetails.resetPasswordPlaceholder", "Parolă nouă")}
                    autoComplete="new-password"
                    disabled={resettingPassword}
                    aria-label={t("userDetails.resetPasswordPlaceholder", "Parolă nouă")!}
                    className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                  />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t("userDetails.confirmPasswordPlaceholder", "Repetă parola")}
                    autoComplete="new-password"
                    disabled={resettingPassword}
                    aria-label={t("userDetails.confirmPasswordPlaceholder", "Repetă parola")!}
                    className={`w-full px-4 py-2.5 bg-white border rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 disabled:opacity-50 ${
                      confirmPassword && newPassword && confirmPassword !== newPassword
                        ? "border-rose-400 focus:ring-rose-500"
                        : "border-slate-300 focus:ring-indigo-500"
                    }`}
                  />
                </div>
                {passwordError && <p className="mt-2 text-xs text-rose-600">{passwordError}</p>}
                <div className="flex justify-end mt-3">
                  <button
                    type="button"
                    onClick={handleSavePassword}
                    disabled={resettingPassword || !newPassword || !confirmPassword}
                    className="px-4 py-2 text-sm font-semibold text-white transition-colors bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {resettingPassword
                      ? t("common.saving", "Se salvează...")
                      : t("userDetails.savePassword", "Salvează parola")}
                  </button>
                </div>
              </SectionCard>

              {/* Coach-specific summary (club, sports, totals) */}
              {isCoach && (
                <SectionCard title={t("userDetails.coachSummary", "Informații antrenor")} bodyPadded>
                  {coachTeamsData && (
                    <StatGrid cols={2} className="mb-4">
                      <StatCard
                        label={t("userDetails.coachTeams", "Echipe asociate")}
                        value={coachTeamsData.total_teams}
                        dotClass="bg-indigo-500"
                      />
                      <StatCard
                        label={t("userDetails.coachAthletes", "Sportivi asociați")}
                        value={coachTeamsData.total_athletes}
                        dotClass="bg-emerald-500"
                      />
                    </StatGrid>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {/* Date added */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 sm:p-4">
                      <div className="flex items-center gap-2 mb-1 sm:mb-2">
                        <Icons.Calendar className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                        <p className="text-xs sm:text-sm font-medium text-slate-500">{t("userDetails.coachAddedAt", "Adăugat la")}</p>
                      </div>
                      <p className="text-sm sm:text-base font-medium text-slate-800">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" }) : "—"}
                      </p>
                    </div>

                    {/* Added via method (best-effort inference) */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 sm:p-4">
                      <div className="flex items-center gap-2 mb-1 sm:mb-2">
                        <Icons.Shield className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                        <p className="text-xs sm:text-sm font-medium text-slate-500">{t("userDetails.coachAddedVia", "Metoda de adăugare")}</p>
                      </div>
                      <p className="text-sm sm:text-base font-medium text-slate-800 break-words">
                        {(user as any).setup_token
                          ? t("userDetails.addedViaSetup", "Invitație administrator (setup neexpirat)")
                          : (user as any).terms_accepted_at
                            ? t("userDetails.addedViaSelfRegister", "Auto-înregistrare")
                            : t("userDetails.addedViaAdmin", "Adăugat de administrator")}
                      </p>
                    </div>

                    {/* Club */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 sm:p-4">
                      <div className="flex items-center gap-2 mb-1 sm:mb-2">
                        <Icons.MapPin className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                        <p className="text-xs sm:text-sm font-medium text-slate-500">{t("userDetails.coachClub", "Club")}</p>
                      </div>
                      <p className="text-sm sm:text-base font-medium text-slate-800 break-words">
                        {coachTeamsData?.coach.club ? (
                          <button
                            onClick={() => navigate(`/admin/clubs/${coachTeamsData.coach.club!.id}`)}
                            className="text-indigo-600 hover:text-indigo-700 hover:underline font-medium text-left"
                          >
                            {coachTeamsData.coach.club.name}
                            {coachTeamsData.coach.club.city && (
                              <span className="ml-1 text-slate-400 font-normal">— {coachTeamsData.coach.club.city}</span>
                            )}
                          </button>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </p>
                    </div>

                    {/* Sports */}
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 sm:p-4 sm:col-span-2">
                      <div className="flex items-center gap-2 mb-1 sm:mb-2">
                        <Icons.Activity className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                        <p className="text-xs sm:text-sm font-medium text-slate-500">{t("userDetails.coachSports", "Sporturi practicate")}</p>
                      </div>
                      {(() => {
                        const certs = coachTeamsData?.coach.certifications;
                        const sportsList: string[] = Array.isArray((certs as any)?.sports)
                          ? (certs as any).sports
                          : Array.isArray((clubDetails[(user.coach_profiles as any)?.club_id] as any)?.sports)
                            ? (clubDetails[(user.coach_profiles as any)?.club_id] as any).sports
                            : [];
                        return <SportChips values={sportsList} />;
                      })()}
                    </div>
                  </div>
                </SectionCard>
              )}

              {/*
                An unverified address is not a warning, it is a locked account:
                login refuses it outright. Shown only while it is true, and with
                both ways out — a new link if the person can be reached, a
                manual confirmation if the old one has already expired.
              */}
              {user.email_verified === false && canChangeThisStatus && (
                <SectionCard title={t("userDetails.emailNotVerified", "Email neconfirmat")} bodyPadded>
                  <p className="text-sm leading-relaxed text-slate-600">
                    {t(
                      "userDetails.emailNotVerifiedBody",
                      "Contul nu poate fi folosit până când adresa nu este confirmată — la autentificare apare „Please verify your email before logging in”. Linkul trimis pe email expiră după 24 de ore."
                    )}
                  </p>
                  <div className="flex flex-wrap gap-3 mt-4">
                    <button
                      type="button"
                      onClick={handleConfirmEmail}
                      disabled={verifyBusy !== null}
                      className="px-4 py-2.5 text-sm font-bold text-white transition-colors bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-60"
                    >
                      {verifyBusy === "confirm"
                        ? t("common.saving", "Se salvează...")
                        : t("userDetails.confirmEmailNow", "Confirmă adresa acum")}
                    </button>
                    <button
                      type="button"
                      onClick={handleResendVerification}
                      disabled={verifyBusy !== null}
                      className="px-4 py-2.5 text-sm font-semibold bg-white border text-slate-700 border-slate-300 rounded-xl hover:bg-slate-50 disabled:opacity-60"
                    >
                      {verifyBusy === "resend"
                        ? t("common.sending", "Se trimite...")
                        : t("userDetails.resendVerification", "Trimite din nou linkul")}
                    </button>
                  </div>
                  <p className="mt-3 text-xs text-slate-400">
                    {t(
                      "userDetails.confirmEmailHint",
                      "Confirmă manual doar dacă știi sigur că adresa îi aparține — de obicei pentru că tu ai creat contul."
                    )}
                  </p>
                </SectionCard>
              )}

              {/* User Status Management */}
              <SectionCard title={t("userDetails.accountStatus", "Status cont")} bodyPadded>
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">{t("userDetails.currentStatus", "Status curent")}:</span>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${statusCfg.chip}`}>
                      <span className={`w-2 h-2 rounded-full ${statusCfg.dot}`}></span>
                      {statusCfg.label}
                    </span>
                  </div>
                  {isSubscriptionAccount && user.status === "demo" && user.demo_expires_at && (
                    <div className="text-sm text-slate-600 bg-indigo-50 border border-indigo-200 px-3 py-1.5 rounded-lg">
                      <span className="font-medium">{t("userDetails.demoExpires", "Expiră")}:</span>{" "}
                      {formatDate(new Date(user.demo_expires_at))}
                      {" "}({Math.max(0, Math.ceil((new Date(user.demo_expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))} {t("userDetails.daysLeft", "zile rămase")})
                    </div>
                  )}
                  {/* Only shown to someone the server would actually let act.
                      A club admin may change a member of their club, but never
                      themselves and never another club admin — so on their own
                      profile these buttons could only ever produce a 403, and
                      "activate / deactivate my own account" is not a decision
                      that belongs to them anyway. */}
                  {canChangeThisStatus && (
                  <div className="flex flex-wrap items-center gap-2 ml-auto">
                    {/* Activate */}
                    {user.status !== "active" && (
                      pendingStatusAction === "active" ? (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <span className="text-sm text-emerald-800">{t("userDetails.confirmActivate", "Sigur activezi acest cont?")}</span>
                          <button
                            disabled={statusBusy}
                            onClick={() => performStatusChange("active")}
                            className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-medium disabled:opacity-50"
                          >
                            {statusBusy ? "..." : t("common.yes", "Da")}
                          </button>
                          <button
                            disabled={statusBusy}
                            onClick={() => setPendingStatusAction(null)}
                            className="px-3 py-1 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded text-xs font-medium disabled:opacity-50"
                          >
                            {t("common.no", "Nu")}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setPendingStatusAction("active")}
                          className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          {t("userDetails.activateAccount", "Activează")}
                        </button>
                      )
                    )}

                    {/* Set Demo */}
                    {isSubscriptionAccount && user.status !== "demo" && (
                      pendingStatusAction === "demo" ? (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-lg">
                          <span className="text-sm text-indigo-800">{t("userDetails.confirmDemo", "Sigur setezi ca Demo (14 zile)?")}</span>
                          <button
                            disabled={statusBusy}
                            onClick={() => performStatusChange("demo")}
                            className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded text-xs font-medium disabled:opacity-50"
                          >
                            {statusBusy ? "..." : t("common.yes", "Da")}
                          </button>
                          <button
                            disabled={statusBusy}
                            onClick={() => setPendingStatusAction(null)}
                            className="px-3 py-1 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded text-xs font-medium disabled:opacity-50"
                          >
                            {t("common.no", "Nu")}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setPendingStatusAction("demo")}
                          className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          {t("userDetails.setDemo", "Setează Demo (14 zile)")}
                        </button>
                      )
                    )}

                    {/* Deactivate */}
                    {user.status !== "inactive" && (
                      pendingStatusAction === "inactive" ? (
                        <div className="flex flex-col gap-2 px-3 py-2 bg-rose-50 border border-rose-200 rounded-lg max-w-md">
                          <span className="text-sm text-rose-800 font-medium">{t("userDetails.confirmDeactivate", "Sigur dezactivezi acest cont?")}</span>
                          {impactLoading && (
                            <span className="text-xs text-rose-700">{t("userDetails.loadingImpact", "Verific impactul...")}</span>
                          )}
                          {impact && (
                            <ul className="text-xs text-rose-700 space-y-0.5">
                              {impact.teams_count > 0 && (
                                <li>• {t("userDetails.impactTeams", "Echipe asociate")}: <strong>{impact.teams_count}</strong></li>
                              )}
                              {impact.athlete_teams_count > 0 && (
                                <li>• {t("userDetails.impactAthleteTeams", "Înscris în echipă")}: <strong>{impact.athlete_teams_count}</strong></li>
                              )}
                              {impact.future_sessions_count > 0 && (
                                <li>• {t("userDetails.impactFutureSessions", "Sesiuni viitoare programate")}: <strong>{impact.future_sessions_count}</strong></li>
                              )}
                              {impact.teams_count === 0 && impact.athlete_teams_count === 0 && impact.future_sessions_count === 0 && (
                                <li className="italic">{t("userDetails.impactNone", "Niciun atașament activ.")}</li>
                              )}
                            </ul>
                          )}
                          <div className="flex gap-2">
                            <button
                              disabled={statusBusy}
                              onClick={() => performStatusChange("inactive")}
                              className="px-3 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded text-xs font-medium disabled:opacity-50"
                            >
                              {statusBusy ? "..." : t("common.yes", "Da, dezactivează")}
                            </button>
                            <button
                              disabled={statusBusy}
                              onClick={() => setPendingStatusAction(null)}
                              className="px-3 py-1 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded text-xs font-medium disabled:opacity-50"
                            >
                              {t("common.no", "Nu")}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setPendingStatusAction("inactive");
                            ensureImpactLoaded();
                          }}
                          className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          {t("userDetails.deactivateAccount", "Dezactivează")}
                        </button>
                      )
                    )}

                    {/* Delete — only available once the account is inactive */}
                    {user.status === "inactive" && (
                      pendingDelete ? (
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-rose-100 border border-rose-300 rounded-lg">
                          <span className="text-sm text-rose-900 font-medium">
                            {t("userDetails.confirmDelete", "Ștergi definitiv contul? Această acțiune nu poate fi anulată.")}
                          </span>
                          <button
                            disabled={deleteBusy}
                            onClick={performDelete}
                            className="px-3 py-1 bg-rose-700 hover:bg-rose-800 text-white rounded text-xs font-medium disabled:opacity-50"
                          >
                            {deleteBusy ? "..." : t("common.yes", "Da")}
                          </button>
                          <button
                            disabled={deleteBusy}
                            onClick={() => setPendingDelete(false)}
                            className="px-3 py-1 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded text-xs font-medium disabled:opacity-50"
                          >
                            {t("common.no", "Nu")}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setPendingDelete(true);
                            ensureImpactLoaded();
                          }}
                          className="px-3 py-1.5 bg-slate-800 hover:bg-rose-700 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                          {t("userDetails.deleteAccount", "Șterge cont")}
                        </button>
                      )
                    )}
                  </div>
                  )}
                </div>

                {/* Status History */}
                {statusHistory.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
                      <Icons.Clock className="w-4 h-4" />
                      {t("userDetails.statusHistory", "Istoric Statusuri")}
                    </h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {statusHistory.map((entry) => (
                        <div key={entry.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg text-sm">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              entry.old_status === "active" ? "bg-emerald-100 text-emerald-700" :
                              entry.old_status === "demo" ? "bg-indigo-100 text-indigo-700" :
                              entry.old_status === "inactive" ? "bg-rose-100 text-rose-700" :
                              "bg-slate-100 text-slate-500"
                            }`}>
                              {entry.old_status || "—"}
                            </span>
                            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              entry.new_status === "active" ? "bg-emerald-100 text-emerald-700" :
                              entry.new_status === "demo" ? "bg-indigo-100 text-indigo-700" :
                              "bg-rose-100 text-rose-700"
                            }`}>
                              {entry.new_status}
                            </span>
                          </div>
                          <div className="flex-1 text-slate-500">
                            {t("userDetails.changedBy", "de")} <span className="font-medium text-slate-700">{entry.changed_by.full_name || entry.changed_by.email}</span>
                            {entry.cascade_count > 0 && (
                              <span className="ml-2 text-amber-600">
                                (+{entry.cascade_count} {t("userDetails.membersAffected", "membri afectați")})
                              </span>
                            )}
                          </div>
                          <div className="text-slate-400 text-xs">
                            {formatDate(new Date(entry.created_at))} {new Date(entry.created_at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {loadingStatusHistory && (
                  <div className="flex items-center gap-2 text-sm text-slate-500 mt-2">
                    <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                    {t("userDetails.loadingHistory", "Se încarcă istoricul...")}
                  </div>
                )}
              </SectionCard>
            </div>
          )}

          {/* Roles Tab */}
          {activeTab === "roles" && (
            <div className="space-y-4 sm:space-y-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
                <div className="min-w-0">
                  <h2 className="text-xl font-bold text-slate-800 leading-6">{t("userDetails.rolesAndMemberships", "Roluri și apartenențe")}</h2>
                  <p className="text-slate-500 text-sm mt-0.5">{t("userDetails.rolesSubtitle", "Rolurile și apartenențele acestui utilizator")}</p>
                </div>
                {/* Action buttons: stack as 2-col grid on mobile so the
                    labels stay readable instead of wrapping mid-word. */}
                <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                  <AdminButton
                    variant="secondary"
                    onClick={openAddToTeamModal}
                    icon={<Icons.Users className="w-4 h-4 flex-shrink-0" />}
                  >
                    <span className="truncate">{t("userDetails.addToTeam", "Adaugă în echipă")}</span>
                  </AdminButton>
                  <AdminButton
                    variant="secondary"
                    onClick={openAddToClubModal}
                    icon={<Icons.Activity className="w-4 h-4 flex-shrink-0" />}
                  >
                    <span className="truncate">{t("userDetails.addToClub", "Adaugă în club")}</span>
                  </AdminButton>
                  <AdminButton
                    variant={showAddRole ? "secondary" : "primary"}
                    onClick={toggleAdvancedRole}
                    className="col-span-2 sm:col-span-1"
                    icon={showAddRole ? <Icons.X className="w-4 h-4 flex-shrink-0" /> : <Icons.Shield className="w-4 h-4 flex-shrink-0" />}
                  >
                    <span className="truncate">{showAddRole ? t("common.cancel", "Anulează") : t("userDetails.advanced", "Avansat")}</span>
                  </AdminButton>
                </div>
              </div>

              {/* Add to Team Modal */}
              {showAddToTeam && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-xl">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-bold text-slate-800">{t("userDetails.addToTeam", "Adaugă în echipă")}</h3>
                      <button onClick={() => setShowAddToTeam(false)} className="p-1 hover:bg-slate-100 rounded-lg">
                        <Icons.X className="w-5 h-5 text-slate-500" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block mb-1.5 text-sm font-medium text-slate-700">{t("userDetails.role", "Rol")}</label>
                        <select
                          value={assignmentRole}
                          onChange={(e) => setAssignmentRole(e.target.value as "athlete" | "coach")}
                          className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          {/* Both roles, always.
                              This used to be constrained to whatever profile
                              the subject already had, so a coach could only be
                              added to a team as a coach. But a coach who also
                              trains in a squad at the same club is an ordinary
                              situation, the data model has always allowed it
                              (user_roles is many-to-many with a scope), and the
                              only thing standing in the way was this dropdown.
                              A second account on a second email was the
                              workaround, and it split their points, attendance
                              and history in two. */}
                          <option value="athlete">{t("userDetails.athlete", "Sportiv")}</option>
                          <option value="coach">{t("userDetails.coach", "Antrenor")}</option>
                        </select>
                        {((isCoach && assignmentRole === "athlete") ||
                          (isAthlete && assignmentRole === "coach")) && (
                          <p className="mt-1.5 text-xs text-indigo-700">
                            {t(
                              "userDetails.dualRoleHint",
                              "Persoana va avea ambele roluri pe același cont și va putea comuta între ele din aplicație. Clubul îl numără o dată ca antrenor și o dată ca sportiv."
                            )}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block mb-1.5 text-sm font-medium text-slate-700">{t("userDetails.selectTeam", "Selectează echipa")}</label>
                        {loadingTeams ? (
                          <div className="text-center py-4 text-slate-500">{t("userDetails.loadingTeams", "Se încarcă echipele...")}</div>
                        ) : availableTeams.length === 0 ? (
                          <div className="text-center py-4 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                            {t("userDetails.noTeamsAvailable", "Nu există echipe în clubul acestui utilizator. Creează una întâi.")}
                          </div>
                        ) : (
                          <select
                            value={selectedTeamId}
                            onChange={(e) => setSelectedTeamId(e.target.value)}
                            className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          >
                            <option value="" className="text-slate-800">{t("userDetails.selectATeam", "Selectează o echipă...")}</option>
                            {availableTeams.map(team => (
                              <option key={team.id} value={team.id} className="text-slate-800">
                                {team.name}
                                {team.sport_type ? ` — ${team.sport_type}` : ""}
                                {team.age_group ? ` (${team.age_group})` : ""}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                      <div className="flex gap-3 pt-2">
                        <AdminButton variant="secondary" className="flex-1" onClick={() => setShowAddToTeam(false)}>
                          {t("common.cancel", "Anulează")}
                        </AdminButton>
                        <AdminButton className="flex-1" onClick={handleAddToTeam}>
                          {t("userDetails.addToTeam", "Adaugă în echipă")}
                        </AdminButton>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Add to Club Modal */}
              {showAddToClub && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-xl">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-bold text-slate-800">{t("userDetails.addToClub", "Adaugă în club")}</h3>
                      <button onClick={() => setShowAddToClub(false)} className="p-1 hover:bg-slate-100 rounded-lg">
                        <Icons.X className="w-5 h-5 text-slate-500" />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block mb-1.5 text-sm font-medium text-slate-700">{t("userDetails.role", "Rol")}</label>
                        <select
                          value={assignmentRole}
                          onChange={(e) => setAssignmentRole(e.target.value as "athlete" | "coach")}
                          className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          {/* Both, for the same reason as the team dialog: a
                              second role is added, never swapped for the
                              first. */}
                          <option value="athlete">{t("userDetails.athlete", "Sportiv")}</option>
                          <option value="coach">{t("userDetails.coach", "Antrenor")}</option>
                        </select>
                        {((isCoach && assignmentRole === "athlete") ||
                          (isAthlete && assignmentRole === "coach")) && (
                          <p className="mt-1.5 text-xs text-indigo-700">
                            {t(
                              "userDetails.dualRoleHint",
                              "Persoana va avea ambele roluri pe același cont și va putea comuta între ele din aplicație. Clubul îl numără o dată ca antrenor și o dată ca sportiv."
                            )}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block mb-1.5 text-sm font-medium text-slate-700">{t("userDetails.selectClub", "Selectează clubul")}</label>
                        {loadingClubs ? (
                          <div className="text-center py-4 text-slate-500">{t("userDetails.loadingClubs", "Se încarcă cluburile...")}</div>
                        ) : availableClubs.length === 0 ? (
                          <div className="text-center py-4 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                            {t("userDetails.noClubsAvailable", "Niciun club disponibil.")}
                          </div>
                        ) : (
                          <select
                            value={selectedClubId}
                            onChange={(e) => setSelectedClubId(e.target.value)}
                            className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          >
                            <option value="" className="text-slate-800">{t("userDetails.selectAClub", "Selectează un club...")}</option>
                            {availableClubs.map((club: any) => (
                              <option key={club.id} value={club.id} className="text-slate-800">
                                {club.name}{club.city ? ` — ${club.city}` : ""}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                      <div className="flex gap-3 pt-2">
                        <AdminButton variant="secondary" className="flex-1" onClick={() => setShowAddToClub(false)}>
                          {t("common.cancel", "Anulează")}
                        </AdminButton>
                        <AdminButton className="flex-1" onClick={handleAddToClub}>
                          {t("userDetails.addToClub", "Adaugă în club")}
                        </AdminButton>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {showAddRole && (
                <div className="p-5 bg-indigo-50 rounded-2xl border border-indigo-200">
                  <h3 className="text-[15px] font-bold text-indigo-800 mb-1">{t("userDetails.advancedRoleAssignment", "Atribuire avansată de roluri")}</h3>
                  <p className="text-xs text-indigo-600/80 mb-4">{t("userDetails.advancedRoleHint", "Alege rolul, tipul de domeniu și ținta.")}</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Every role that can be granted from here. Adding a role
                        never REMOVES one, so offering athlete to a coach cannot
                        "turn them into" an athlete — it makes them both, which
                        is a real thing a club needs. platform_admin stays out:
                        that is platform-admin-only territory. */}
                    <select
                      value={newRole.code}
                      onChange={(e) => setNewRole({...newRole, code: e.target.value})}
                      className="px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="athlete">{t("userDetails.athlete", "Sportiv")}</option>
                      <option value="coach">{t("userDetails.coach", "Antrenor")}</option>
                      <option value="club_admin">{t("userDetails.clubAdmin", "Administrator club")}</option>
                    </select>
                    {/* Scope — platform scope removed; only club + team. */}
                    <select
                      value={newRole.scope_type}
                      onChange={(e) => setNewRole({...newRole, scope_type: e.target.value as any, scope_id: ""})}
                      className="px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    >
                      <option value="club">{t("userDetails.club", "Club")}</option>
                      <option value="team">{t("userDetails.team", "Echipă")}</option>
                    </select>
                    {/* scope_id picker — dropdown of clubs OR teams instead of
                        a raw UUID input. Loaded on open via toggleAdvancedRole(). */}
                    {newRole.scope_type === "club" ? (
                      <select
                        value={newRole.scope_id}
                        onChange={(e) => setNewRole({...newRole, scope_id: e.target.value})}
                        disabled={loadingClubs}
                        className="px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
                      >
                        <option value="" className="text-slate-800">
                          {loadingClubs
                            ? t("userDetails.loadingClubs", "Se încarcă cluburi...")
                            : t("userDetails.selectAClub", "Selectează un club...")}
                        </option>
                        {availableClubs.map((club: any) => (
                          <option key={club.id} value={club.id} className="text-slate-800">
                            {club.name}{club.city ? ` — ${club.city}` : ""}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <select
                        value={newRole.scope_id}
                        onChange={(e) => setNewRole({...newRole, scope_id: e.target.value})}
                        disabled={loadingTeams}
                        className="px-3 py-2 text-sm bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
                      >
                        <option value="" className="text-slate-800">
                          {loadingTeams
                            ? t("userDetails.loadingTeams", "Se încarcă echipe...")
                            : t("userDetails.selectATeam", "Selectează o echipă...")}
                        </option>
                        {availableTeams.map(team => (
                          <option key={team.id} value={team.id} className="text-slate-800">
                            {team.name}
                            {team.sport_type ? ` — ${team.sport_type}` : ""}
                            {team.age_group ? ` (${team.age_group})` : ""}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div className="mt-4">
                    <AdminButton
                      onClick={handleAddRole}
                      disabled={!newRole.scope_id}
                      icon={<Icons.CheckCircle className="w-4 h-4" />}
                    >
                      {t("userDetails.assignRole", "Atribuie rol")}
                    </AdminButton>
                  </div>
                </div>
              )}

              {user.roles?.length ? (
                <div className="space-y-2 sm:space-y-3">
                  {user.roles.map(role => (
                    <div key={role.id} className="p-3 sm:p-4 bg-white rounded-2xl border border-slate-200 shadow-sm flex items-start sm:items-center gap-3 sm:gap-4">
                      <div className="p-2 sm:p-3 bg-indigo-100 rounded-lg sm:rounded-xl flex-shrink-0">
                        <Icons.Shield className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-800 text-sm sm:text-base">{t(`roles.${role.code}`, role.label)}</span>
                          <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-full bg-slate-100 text-slate-600 border border-slate-200 capitalize">
                            {role.scope_type === "platform"
                              ? t("userDetails.scopePlatform", "platformă")
                              : role.scope_type === "club"
                                ? t("userDetails.scopeClub", "club")
                                : t("userDetails.scopeTeam", "echipă")}
                          </span>
                        </div>
                        <div className="text-xs sm:text-sm text-slate-500 break-words mt-0.5">
                            {role.scope_type === "platform" && t("userDetails.scopePlatformDesc", "Acces la nivel de platformă")}
                            {role.scope_type === "club" && (() => {
                              const c = role.scope_id ? clubDetails[role.scope_id] : null;
                              if (!c) {
                                return (
                                  <span>
                                    {role.scope_id && <span className="text-slate-400">({role.scope_id.slice(0, 8)}…)</span>}
                                  </span>
                                );
                              }
                              return (
                                <span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); navigate(`/admin/clubs/${c.id}`); }}
                                    className="text-indigo-600 hover:text-indigo-700 hover:underline font-medium"
                                  >
                                    {c.name}
                                  </button>
                                  {c.city && <span className="text-slate-400"> — {c.city}</span>}
                                </span>
                              );
                            })()}
                            {role.scope_type === "team" && (() => {
                              const team = role.scope_id ? teamDetails[role.scope_id] : null;
                              if (!team) {
                                return (
                                  <span>
                                    {role.scope_id && <span className="text-slate-400">({role.scope_id.slice(0, 8)}…)</span>}
                                  </span>
                                );
                              }
                              // Show every coach attached to this team (head
                              // coach via teams.coach_id + everyone with a
                              // team-scoped coach role on this team) minus
                              // the page subject themselves — so the list
                              // doesn't echo back the user we're already on.
                              const allCoaches = (team.coaches && team.coaches.length > 0)
                                ? team.coaches
                                : team.coach
                                  ? [team.coach]
                                  : [];
                              const otherCoaches = allCoaches.filter(c => c.id !== userId);
                              return (
                                <span>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); navigate(`/dashboard/admin-club/teams/${team.id}`); }}
                                    className="text-indigo-600 hover:text-indigo-700 hover:underline font-medium"
                                  >
                                    {team.name}
                                  </button>
                                  {team.sport_type && (
                                    <>
                                      {" · "}
                                      <span className="text-slate-500">{team.sport_type}</span>
                                    </>
                                  )}
                                  {team.age_group && (
                                    <>
                                      {" · "}
                                      <span className="text-slate-500">{team.age_group}</span>
                                    </>
                                  )}
                                  {otherCoaches.length > 0 && (
                                    <>
                                      {" · "}
                                      <span className="text-slate-500">
                                        {otherCoaches.length === 1
                                          ? t("userDetails.coachLabel", "antrenor")
                                          : t("userDetails.coachesLabel", "antrenori")}:
                                      </span>{" "}
                                      {otherCoaches.map((c, idx) => (
                                        <React.Fragment key={c.id}>
                                          {idx > 0 && <span className="text-slate-400">, </span>}
                                          <button
                                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/users/${c.id}`); }}
                                            className="text-indigo-600 hover:text-indigo-700 hover:underline"
                                          >
                                            {c.full_name || c.email}
                                          </button>
                                        </React.Fragment>
                                      ))}
                                    </>
                                  )}
                                </span>
                              );
                            })()}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveRole(role.id)}
                        className="inline-flex items-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-2 bg-rose-100 text-rose-600 hover:bg-rose-200 rounded-lg sm:rounded-xl text-sm font-medium transition-colors flex-shrink-0"
                        title={t("common.remove", "Elimină")}
                        aria-label={t("common.remove", "Elimină")}
                      >
                        <Icons.Trash2 className="w-4 h-4" />
                        <span className="hidden sm:inline">{t("common.remove", "Elimină")}</span>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <AdminCard className="py-12 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-slate-100 flex items-center justify-center">
                    <Icons.Shield className="w-6 h-6 text-slate-400" />
                  </div>
                  <p className="text-slate-500 font-medium">{t("userDetails.noRoles", "Niciun rol atribuit acestui utilizator")}</p>
                </AdminCard>
              )}
            </div>
          )}

          {/* Teams Tab (for coaches) */}
          {activeTab === "teams" && isCoach && (
            <div className="space-y-4 sm:space-y-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
                <div className="min-w-0">
                  <h2 className="text-xl font-bold text-slate-800 leading-6">{t("userDetails.teamsAndAthletes", "Echipe și sportivi")}</h2>
                  <p className="text-slate-500 text-sm mt-0.5">{t("userDetails.teamsSubtitle", "Echipele coordonate de acest antrenor și sportivii lor")}</p>
                </div>
                <AdminButton onClick={openAllocateTeamsModal} icon={<Icons.Edit className="w-4 h-4 flex-shrink-0" />}>
                  {t("userDetails.allocateTeams", "Alocă echipe")}
                </AdminButton>
              </div>

              {coachTeamsData && (
                <StatGrid cols={2}>
                  <StatCard
                    label={t("userDetails.teamsCount", "Echipe")}
                    value={coachTeamsData.total_teams}
                    dotClass="bg-indigo-500"
                  />
                  <StatCard
                    label={t("userDetails.athletesCount", "Sportivi")}
                    value={coachTeamsData.total_athletes}
                    dotClass="bg-emerald-500"
                  />
                </StatGrid>
              )}

              {/* Team Allocation Modal */}
              {showAllocateTeams && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4 shadow-xl max-h-[80vh] overflow-y-auto">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-bold text-slate-800">{t("userDetails.allocateTeamsToCoach", "Alocă echipe antrenorului")}</h3>
                      <button onClick={() => setShowAllocateTeams(false)} className="p-1 hover:bg-slate-100 rounded-lg">
                        <Icons.X className="w-5 h-5 text-slate-500" />
                      </button>
                    </div>
                    <p className="text-sm text-slate-500 mb-4">
                      {t("userDetails.allocateTeamsDesc", "Selectați echipele pe care acest antrenor ar trebui să le gestioneze. Comutați echipele pentru a le aloca sau dezaloca.")}
                    </p>
                    {loadingTeams ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                      </div>
                    ) : clubTeamsForAllocation.length > 0 ? (
                      <div className="space-y-2">
                        {clubTeamsForAllocation.map((team) => {
                          const isAssignedToThisCoach = team.coach_id === userId;
                          const isAssignedToOther = team.coach_id && team.coach_id !== userId;
                          const isAllocating = allocatingTeam === team.id;
                          return (
                            <div
                              key={team.id}
                              className={`p-4 rounded-xl border ${
                                isAssignedToThisCoach
                                  ? "bg-indigo-50 border-indigo-200"
                                  : isAssignedToOther
                                  ? "bg-slate-50 border-slate-200 opacity-60"
                                  : "bg-white border-slate-200 hover:border-indigo-300"
                              } transition-all`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-slate-800">{team.name}</h4>
                                  <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                                    {team.sport_type && <span>{team.sport_type}</span>}
                                    {team.age_group && <span>• {team.age_group}</span>}
                                    {team._count && <span>• {team._count.athlete_profiles} {t("userDetails.athletesCount", "sportivi")}</span>}
                                  </div>
                                  {isAssignedToOther && team.coach && (
                                    <p className="text-xs text-amber-600 mt-1">
                                      {t("userDetails.currentlyAssignedTo", "Alocat momentan lui:")} {team.coach.full_name || team.coach.email}
                                    </p>
                                  )}
                                </div>
                                <button
                                  onClick={() => toggleTeamAllocation(team.id, isAssignedToThisCoach)}
                                  disabled={isAllocating}
                                  className={`ml-4 px-4 py-2 rounded-xl font-medium text-sm transition-colors ${
                                    isAssignedToThisCoach
                                      ? "bg-indigo-600 text-white hover:bg-indigo-700"
                                      : "bg-white border-2 border-indigo-600 text-indigo-700 hover:bg-indigo-600 hover:text-white"
                                  } disabled:opacity-50`}
                                >
                                  {isAllocating ? (
                                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                                  ) : isAssignedToThisCoach ? (
                                    <span className="flex items-center gap-1">
                                      <Icons.CheckCircle className="w-4 h-4" />
                                      {t("userDetails.assigned", "Alocat")}
                                    </span>
                                  ) : (
                                    t("userDetails.assign", "Alocă")
                                  )}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-400">
                        {t("userDetails.noTeamsInClub", "Nu există echipe în acest club")}
                      </div>
                    )}
                    <div className="mt-6 pt-4 border-t border-slate-200">
                      <AdminButton variant="secondary" className="w-full" onClick={() => setShowAllocateTeams(false)}>
                        {t("common.close", "Închide")}
                      </AdminButton>
                    </div>
                  </div>
                </div>
              )}

              {loadingCoachTeams ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                </div>
              ) : coachTeamsData?.teams.length ? (
                <div className="space-y-4 sm:space-y-6">
                  {coachTeamsData.teams.map((team) => (
                    <div key={team.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                      {/* Team Header — stack on mobile so the title and the
                          athlete count badge don't fight for space. */}
                      <div className="p-3 sm:p-5 bg-gradient-to-r from-indigo-50 to-violet-50 border-b border-slate-200">
                        <div className="flex items-start gap-3 sm:gap-4">
                          <div className="p-2 sm:p-3 bg-indigo-100 rounded-lg sm:rounded-xl flex-shrink-0">
                            <Icons.Users className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                              <h3 className="text-base sm:text-lg font-semibold text-slate-800 break-words min-w-0">{team.name}</h3>
                              <span className="self-start inline-flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 bg-indigo-100 text-indigo-700 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap">
                                <Icons.Activity className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                {team.athlete_count} {t("userDetails.athletesCount", "sportivi")}
                              </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs sm:text-sm text-slate-500 mt-1">
                              {team.sport_type && <span>{team.sport_type}</span>}
                              {team.age_group && <span>• {team.age_group}</span>}
                              {team.club && <span>• {team.club.name}</span>}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Athletes List */}
                      {team.athletes.length > 0 ? (
                        <div className="divide-y divide-slate-100">
                          {team.athletes.map((athlete) => (
                            <div
                              key={athlete.id}
                              className="p-3 sm:p-4 hover:bg-slate-100 transition-colors cursor-pointer"
                              onClick={() => navigate(`/admin/users/${athlete.id}`)}
                            >
                              <div className="flex items-start gap-3 sm:gap-4">
                                {/* Avatar */}
                                {athlete.profile_url ? (
                                  <img
                                    src={athlete.profile_url}
                                    alt={athlete.full_name || athlete.email}
                                    className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                  />
                                ) : (
                                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                                    {(athlete.full_name || athlete.email).charAt(0).toUpperCase()}
                                  </div>
                                )}
                                {/* Info — name + meta row + secondary contact.
                                    Meta (age + vulnerable badge) sits inline
                                    next to the name on sm+, drops below on
                                    mobile to avoid the squashed 3-column
                                    layout. */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-3">
                                    <p className="font-medium text-slate-800 truncate text-sm sm:text-base">
                                      {athlete.full_name || athlete.email}
                                    </p>
                                    <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm">
                                      {athlete.date_of_birth && calculateAge(athlete.date_of_birth) !== null && (
                                        <span className="text-slate-500 whitespace-nowrap">
                                          {t("userDetails.age", "Vârstă")}: {calculateAge(athlete.date_of_birth)}
                                        </span>
                                      )}
                                      {athlete.vulnerability_flag && (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap">
                                          <Icons.AlertTriangle className="w-3 h-3" />
                                          {t("userDetails.vulnerable", "Vulnerabil")}
                                        </span>
                                      )}
                                      {/* Signed up through a shared link and
                                          still waiting. They are shown on the
                                          roster so the decision is where the
                                          people are, not on another page. */}
                                      {athlete.pending_membership && (
                                        <>
                                          <span className="inline-flex items-center px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-[10px] sm:text-xs font-medium whitespace-nowrap">
                                            {t("userDetails.awaitingApproval", "Așteaptă acceptul")}
                                          </span>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              approveJoin(athlete.pending_membership!.id);
                                            }}
                                            disabled={approvingJoinId === athlete.pending_membership.id}
                                            className="px-2.5 py-1 text-[11px] sm:text-xs font-bold text-white rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap"
                                          >
                                            {approvingJoinId === athlete.pending_membership.id
                                              ? "..."
                                              : t("userDetails.approve", "Acceptă")}
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  {/* Not the email: these are children, and a
                                      coach scanning a roster needs to know how
                                      old they are and since when they train
                                      here, not how to write to them. */}
                                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs sm:text-sm text-slate-500 mt-1">
                                    {athlete.date_of_birth && calculateAge(athlete.date_of_birth) !== null && (
                                      <span className="flex items-center gap-1">
                                        <Icons.User className="w-3 h-3 flex-shrink-0" />
                                        {calculateAge(athlete.date_of_birth)} {t("userDetails.years", "ani")}
                                      </span>
                                    )}
                                    {athlete.joined_team_at && (
                                      <span className="flex items-center gap-1">
                                        <Icons.Calendar className="w-3 h-3 flex-shrink-0" />
                                        {t("userDetails.memberSince", "înscris din")} {formatDate(new Date(athlete.joined_team_at))}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 sm:p-6 text-center text-sm sm:text-base text-slate-400">
                          {t("userDetails.noAthletesInTeam", "Niciun sportiv în această echipă încă")}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <AdminCard className="py-12 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-slate-100 flex items-center justify-center">
                    <Icons.Users className="w-6 h-6 text-slate-400" />
                  </div>
                  <p className="text-slate-500 font-medium">{t("userDetails.noTeamsAssigned", "Nicio echipă alocată acestui antrenor încă")}</p>
                  <p className="text-sm text-slate-400 mt-1">
                    {t("userDetails.assignCoachToTeams", "Alocați acest antrenor la echipe pentru a le vedea aici")}
                  </p>
                </AdminCard>
              )}
            </div>
          )}

          {/* Profile Tab */}
          {activeTab === "profile" && isAthlete && user.athlete_profiles && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-slate-800 mb-6">{t("userDetails.athleteProfile", "Profil sportiv")}</h2>
              {(() => {
                const profile = user.athlete_profiles;
                const club = clubDetails[profile.club_id];
                const age = calculateAge(user.date_of_birth);
                return (
                  <div className="space-y-6">
                    {/* Athlete Quick Info - Age Badge */}
                    {(age !== null || user.date_of_birth) && (
                      <div className="p-5 bg-gradient-to-r from-indigo-50 to-violet-50 rounded-xl border border-indigo-200">
                        <div className="flex items-center gap-6">
                          <div className="flex items-center gap-3">
                            <div className="p-3 bg-indigo-100 rounded-xl">
                              <Icons.Calendar className="w-6 h-6 text-indigo-600" />
                            </div>
                            <div>
                              <p className="text-sm text-indigo-600 font-medium">{t("userDetails.age", "Vârstă")}</p>
                              <p className="text-3xl font-bold text-indigo-800">
                                {age !== null ? `${age} ${t("userDetails.years", "ani")}` : "—"}
                              </p>
                            </div>
                          </div>
                          {user.date_of_birth && (
                            <div className="text-sm text-slate-500">
                              {t("userDetails.born", "Născut:")} {new Date(user.date_of_birth).toLocaleDateString("ro-RO", {
                                day: "numeric",
                                month: "long",
                                year: "numeric"
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Club Information */}
                    <div className="p-6 bg-white rounded-2xl border border-slate-200 shadow-sm">
                      <h3 className="font-semibold text-indigo-700 mb-4 flex items-center gap-2">
                        <Icons.Users className="w-5 h-5" />
                        {t("userDetails.clubInformation", "Informații club")}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm text-slate-500 mb-1">{t("userDetails.clubName", "Nume club")}</p>
                          <p className="text-lg font-medium text-slate-800">{club?.name || t("common.loading", "Se încarcă...")}</p>
                        </div>
                        {club?.city && (
                          <div>
                            <p className="text-sm text-slate-500 mb-1">{t("userDetails.location", "Locație")}</p>
                            <p className="text-slate-700 flex items-center gap-2">
                              <Icons.MapPin className="w-4 h-4 text-indigo-500" />
                              {[club.city, club.county, club.country].filter(Boolean).join(", ")}
                            </p>
                          </div>
                        )}
                        {profile.team && (
                          <div>
                            <p className="text-sm text-slate-500 mb-1">{t("common.team", "Echipă")}</p>
                            <p className="text-slate-700">{profile.team.name}</p>
                          </div>
                        )}
                        {profile.team?.coach && (
                          <div>
                            <p className="text-sm text-slate-500 mb-1">{t("userDetails.coach", "Antrenor")}</p>
                            <p className="text-slate-700">{profile.team.coach.full_name || profile.team.coach.email}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-sm text-slate-500 mb-1">{t("userDetails.joinedAt", "Data înscrierii")}</p>
                          <p className="text-slate-700 flex items-center gap-2">
                            <Icons.Calendar className="w-4 h-4 text-indigo-500" />
                            {formatDate(new Date(profile.joined_at))}
                          </p>
                        </div>
                      </div>
                      {club?.sports && club.sports.length > 0 && (
                        <div className="mt-6">
                          <p className="text-sm text-slate-500 mb-3">{t("userDetails.sportsPracticed", "Sporturi practicate")}</p>
                          <SportChips
                            values={club.sports}
                            className="gap-2"
                            chipClassName="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium border border-indigo-200"
                          />
                        </div>
                      )}
                    </div>

                    {/* Guardian Information */}
                    {profile.guardian_contact && Object.keys(profile.guardian_contact).length > 0 && (
                      <div className="p-6 bg-white rounded-2xl border border-slate-200 shadow-sm">
                        <h3 className="font-semibold text-indigo-700 mb-4 flex items-center gap-2">
                          <Icons.Users className="w-5 h-5" />
                          {t("userDetails.guardianInfo", "Informații tutore / părinte")}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {profile.guardian_contact.name && (
                            <div>
                              <p className="text-sm text-slate-500 mb-1">{t("common.name", "Nume")}</p>
                              <p className="text-slate-700">{profile.guardian_contact.name}</p>
                            </div>
                          )}
                          {profile.guardian_contact.phone && (
                            <div>
                              <p className="text-sm text-slate-500 mb-1">{t("userDetails.phone", "Telefon")}</p>
                              <p className="text-slate-700 flex items-center gap-2">
                                <Icons.Phone className="w-4 h-4 text-indigo-500" />
                                {profile.guardian_contact.phone}
                              </p>
                            </div>
                          )}
                          {profile.guardian_contact.email && (
                            <div>
                              <p className="text-sm text-slate-500 mb-1">{t("common.email", "Email")}</p>
                              <p className="text-slate-700 flex items-center gap-2">
                                <Icons.Mail className="w-4 h-4 text-indigo-500" />
                                {profile.guardian_contact.email}
                              </p>
                            </div>
                          )}
                          {profile.guardian_contact.relationship && (
                            <div>
                              <p className="text-sm text-slate-500 mb-1">{t("userDetails.relationship", "Relație")}</p>
                              <p className="text-slate-700">{profile.guardian_contact.relationship}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Vulnerability Flag */}
                    {profile.vulnerability_flag && (
                      <div className="p-5 bg-amber-50 border border-amber-200 rounded-xl">
                        <div className="flex items-start gap-4">
                          <div className="p-2 bg-amber-100 rounded-lg">
                            <Icons.AlertTriangle className="w-5 h-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-amber-800 mb-1">{t("userDetails.vulnerabilityFlagActive", "Marcaj de vulnerabilitate activ")}</p>
                            {profile.vulnerability_notes && (
                              <p className="text-sm text-amber-700">{profile.vulnerability_notes}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Sessions Tab */}
          {activeTab === "sessions" && isAthlete && (
            <div className="space-y-4 sm:space-y-6">
              <h2 className="text-lg sm:text-xl font-bold text-slate-800 mb-3 sm:mb-6">{t("userDetails.activitiesAndSessions", "Activități & Sesiuni")}</h2>
              {loadingSessions ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                </div>
              ) : sessionsData ? (
                <>
                  {/* Session Stats — uniform stat cards (no leading icon) so the
                      "Total" card matches the per-status cards visually. */}
                  <StatGrid cols={4} className="mb-4 sm:mb-6">
                    <StatCard label={t("userDetails.totalSessions", "Total")} value={sessionsData.total} dotClass="bg-indigo-500" />
                    {sessionsData.stats.map(stat => (
                      <StatCard key={stat.status} label={t(`attendance.${stat.status}`, stat.status)} value={stat.count} />
                    ))}
                  </StatGrid>

                  {/* Session List */}
                  <div className="space-y-2 sm:space-y-3">
                    {sessionsData.attendance.map(record => (
                      <div key={record.id} className="p-3 sm:p-5 bg-white rounded-2xl border border-slate-200 shadow-sm">
                        <div className="flex justify-between items-start gap-2 mb-2 sm:mb-3">
                          <div className="min-w-0 flex-1">
                            <h3 className="font-semibold text-slate-800 text-sm sm:text-base break-words">{record.session.title}</h3>
                            <p className="text-xs sm:text-sm text-slate-500">{t("common.team", "Echipă")}: {record.session.team.name}</p>
                          </div>
                          <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium flex-shrink-0 ${getStatusBadge(record.status)}`}>
                            {t(`attendance.${record.status}`, record.status)}
                          </span>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-500 flex items-center gap-2">
                          <Icons.Clock className="w-4 h-4 flex-shrink-0" />
                          <span>{new Date(record.session.start_at).toLocaleString("ro-RO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <AdminCard className="py-12 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-slate-100 flex items-center justify-center">
                    <Icons.Activity className="w-6 h-6 text-slate-400" />
                  </div>
                  <p className="text-slate-500 font-medium">{t("userDetails.noSessionData", "Nu există date despre sesiuni")}</p>
                </AdminCard>
              )}
            </div>
          )}

          {/* Points Tab */}
          {activeTab === "points" && isAthlete && (
            <div className="space-y-6">
              <div className="flex justify-between items-center gap-3 flex-wrap mb-6">
                <h2 className="text-xl font-bold text-slate-800">{t("userDetails.pointsHistory", "Istoric puncte")}</h2>
                <div className="flex items-center gap-3">
                  {pointsData && (
                    <div className="flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-amber-100 to-amber-50 rounded-xl border border-amber-200">
                      <Icons.Star className="w-6 h-6 text-amber-500" />
                      <span className="text-2xl font-bold text-amber-700">{pointsData.totalPoints.toLocaleString()}</span>
                      <span className="text-sm text-amber-600">{t("userDetails.totalPointsLabel", "total puncte")}</span>
                    </div>
                  )}
                  <button
                    onClick={() => { setShowAdjust((v) => !v); setAdjustError(null); }}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm shadow-indigo-500/20 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    {t("userDetails.adjustPoints", "Ajustează puncte")}
                  </button>
                </div>
              </div>

              {/* Manual adjustment form */}
              {showAdjust && (
                <form onSubmit={handleAdjustPoints} className="mb-6 p-5 bg-white rounded-2xl border border-slate-200 shadow-sm space-y-4">
                  <div className="flex flex-wrap items-end gap-4">
                    {/* add / subtract */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{t("userDetails.adjustType", "Operație")}</label>
                      <div className="inline-flex bg-slate-100 rounded-xl p-1">
                        <button
                          type="button"
                          onClick={() => setAdjustSign("add")}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${adjustSign === "add" ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500"}`}
                        >
                          + {t("userDetails.adjustAdd", "Adaugă")}
                        </button>
                        <button
                          type="button"
                          onClick={() => setAdjustSign("subtract")}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${adjustSign === "subtract" ? "bg-white text-rose-700 shadow-sm" : "text-slate-500"}`}
                        >
                          − {t("userDetails.adjustSubtract", "Scade")}
                        </button>
                      </div>
                    </div>
                    {/* amount */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{t("userDetails.adjustAmount", "Puncte")}</label>
                      <input
                        type="number"
                        min={1}
                        value={adjustAmount}
                        onChange={(e) => setAdjustAmount(e.target.value)}
                        placeholder="ex: 50"
                        className="w-28 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 tabular-nums focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                    {/* reason */}
                    <div className="flex-1 min-w-[220px]">
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{t("userDetails.adjustReason", "Motiv")}</label>
                      <select
                        value={adjustReasonPreset}
                        onChange={(e) => setAdjustReasonPreset(e.target.value)}
                        className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      >
                        <option value="">{t("userDetails.adjustReasonSelect", "Alege un motiv…")}</option>
                        {POINTS_REASON_PRESETS.map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {adjustReasonPreset === "custom" && (
                    <input
                      type="text"
                      value={adjustCustomReason}
                      onChange={(e) => setAdjustCustomReason(e.target.value)}
                      placeholder={t("userDetails.adjustReasonCustom", "Scrie motivul…")}
                      className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  )}

                  {adjustError && <p className="text-sm text-rose-600">{adjustError}</p>}

                  <div className="flex items-center gap-2">
                    <button
                      type="submit"
                      disabled={adjusting}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50 transition-colors"
                    >
                      {adjusting ? t("common.saving", "Se salvează…") : t("userDetails.adjustApply", "Aplică ajustarea")}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowAdjust(false); resetAdjustForm(); }}
                      className="px-4 py-2.5 rounded-xl font-medium text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
                    >
                      {t("common.cancel", "Anulează")}
                    </button>
                  </div>
                </form>
              )}

              {loadingPoints ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                </div>
              ) : pointsData?.points?.length ? (
                <div className="space-y-3">
                  {pointsData.points.map(entry => {
                    const pts = entry.points ?? 0;
                    return (
                      <div key={entry.id} className="p-5 bg-white rounded-2xl border border-slate-200 shadow-sm flex justify-between items-center">
                        <div>
                          <h3 className="font-semibold text-slate-800">{formatActionType(entry.action_type)}</h3>
                          {entry.note && <p className="text-sm text-slate-600 italic mt-0.5">"{entry.note}"</p>}
                          <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                            <Icons.Clock className="w-4 h-4" />
                            {entry.created_at ? new Date(entry.created_at).toLocaleString() : "—"}
                          </p>
                        </div>
                        <div className={`text-2xl font-bold ${pts > 0 ? "text-emerald-600" : "text-rose-600"}`}>
                          {pts > 0 ? "+" : ""}{pts}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <AdminCard className="py-12 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-slate-100 flex items-center justify-center">
                    <Icons.Star className="w-6 h-6 text-slate-400" />
                  </div>
                  <p className="text-slate-500 font-medium">{t("userDetails.noPointsHistory", "Nu există istoric de puncte")}</p>
                </AdminCard>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
